import {
  FileText,
  FolderOpen,
  Layers3,
  MapPinned,
  Users,
  Workflow,
} from "lucide-react";

export type V2ProofStat = {
  value: string;
  label: string;
};

export type V2Testimonial = {
  quote: string;
  initials: string;
  name: string;
  role: string;
  location: string;
  image?: string;
};

export const v2ProofStats: readonly V2ProofStat[] = [
  { value: "2.000+", label: "clientes atendidos em todo o Brasil" },
  { value: "3.500+", label: "licenças GeoOffice V2 ativas" },
  { value: "20 min", label: "para um georreferenciamento completo" },
  { value: "100%", label: "offline, em qualquer licença" },
];

export const v2Testimonials: readonly V2Testimonial[] = [
  {
    quote:
      "O que mais pesa pra mim é não depender de internet em obra. Rodo o GeoOffice V2 direto no computador de campo e nunca travou um projeto.",
    initials: "MT",
    name: "Marcos T.",
    role: "Topógrafo",
    location: "MG",
  },
  {
    quote:
      "Comecei só no Georreferenciamento e fui subindo de módulo conforme os projetos pediam. Paguei a diferença, não o valor cheio de novo.",
    initials: "RA",
    name: "Renata A.",
    role: "Engenheira Civil",
    location: "SP",
  },
  {
    quote:
      "Migrei de outro software e o suporte fez toda a diferença — respondem rápido e entendem o que a gente faz na prática.",
    initials: "DM",
    name: "Diego M.",
    role: "Agrimensor",
    location: "GO",
  },
];

export const v2Modules = [
  {
    name: "Georreferenciamento",
    description:
      "A base do GeoOffice — georreferenciamento completo de imóveis rurais e urbanos, com conformidade INCRA/SIGEF.",
    details: [
      "CAD próprio, com comandos similares ao AutoCAD",
      "Cadernetas de campo",
      "Cálculo de poligonais (MMQ incluso)",
      "Cálculo e divisão de áreas",
      "Memoriais descritivos automáticos",
      "Reconstituição de memoriais",
      "Auto croqui",
      "Loteamento / Reurb automático",
      "Geodésia",
      "Monografia de marco",
      "Imagens vetorial, raster, satélite e drone",
      "Nuvem de pontos",
      "MDT / Polimesh",
      "Curvas de nível",
      "Conformidade INCRA (Lei 10.267/01), com geração automática de layers NTGIR",
      "Planta de Situação automática via Google Maps",
      "Integração com Google Earth",
      "Relatórios SIGEF (.ods)",
      "Exportação SHP para CAR / SICAR / RI-Digital",
    ],
  },
  {
    name: "Projetos",
    included: "+ tudo do módulo anterior",
    description:
      "Para quem avança do levantamento para o projeto de engenharia — perfis, seções e volumes.",
    details: [
      "Perfil longitudinal e seções transversais",
      "Visualizador 3D",
      "Volume entre superfícies",
      "Cálculo de inundação",
      "Greide",
      "Curvas verticais e horizontais",
      "Seções-tipo",
      "Volume entre camadas",
      "Mapa de declividades",
      "Platô (taludes, offsets, bermas)",
    ],
  },
  {
    name: "Estradas",
    included: "+ tudo dos módulos anteriores",
    description: "Projeto viário completo — do traçado à pavimentação.",
    details: [
      "Superelevação e superlargura",
      "Curvas de transição",
      "Diagrama de massas (Brückner)",
      "Dimensionamento de pavimentação (IG, CBR, DNER)",
      "Relatórios de terraplenagem, locação e pavimentação",
    ],
  },
  {
    name: "Agricultura de Precisão",
    included: "+ tudo dos módulos anteriores",
    description:
      "Agricultura de precisão com integração direta a pilotos automáticos de mercado.",
    details: [
      "Importação/exportação shape com conversão automática de DATUM",
      "Integração com pilotos automáticos (Trimble, Topcon, John Deere/AMS, CNH/AFS)",
      "Filtros de precisão",
      "Projeto de plantio e colheita automáticos",
      "Comparativo projetado × realizado",
    ],
  },
] as const;

export const v2Gallery = [
  [
    "Projetos viários",
    "Do alinhamento ao detalhamento de estradas.",
    "/images/foto-trial7.jpg",
  ],
  [
    "Modelagem tridimensional",
    "Superfícies e volumes para decisões precisas.",
    "/images/foto-trial8.jpg",
  ],
  [
    "Plantas técnicas",
    "Documentação organizada para entregar com confiança.",
    "/images/foto-trial12.jpg",
  ],
  [
    "Memoriais descritivos",
    "Resultados técnicos dentro do mesmo fluxo.",
    "/images/foto-trial15.jpg",
  ],
  [
    "Perfis e seções",
    "Leitura técnica para projeto e execução.",
    "/images/foto-trial18.jpg",
  ],
  [
    "Relatórios técnicos",
    "Documentos organizados para revisar e entregar.",
    "/images/foto-trial24.jpg",
  ],
] as const;

export const platformJourneys = [
  {
    icon: FolderOpen,
    step: "01",
    title: "Centralize o trabalho",
    description:
      "Organize arquivos técnicos e mantenha cada projeto em um contexto fácil de localizar.",
  },
  {
    icon: Users,
    step: "02",
    title: "Defina quem participa",
    description:
      "Separe equipes, clientes e projetos em workspaces com acessos próprios.",
  },
  {
    icon: Workflow,
    step: "03",
    title: "Resolva tarefas online",
    description:
      "Use aplicações específicas sem tirar arquivos e resultados do ambiente de trabalho.",
  },
] as const;

export const platformTools = [
  {
    icon: FileText,
    title: "Memorial SIGEF",
    description: "Transforme dados do trabalho em memoriais organizados.",
    result: "Documento técnico pronto para revisar e usar no seu fluxo.",
  },
  {
    icon: MapPinned,
    title: "Utilitário ONR",
    description: "Prepare coordenadas para os fluxos do RI Digital.",
    result: "Dados formatados com mais consistência antes do envio.",
  },
  {
    icon: Workflow,
    title: "Reconstituição",
    description: "Reconstrua áreas e perímetros a partir de memoriais.",
    result: "Geometria visual para conferir o perímetro reconstruído.",
  },
  {
    icon: Layers3,
    title: "Cena 3D",
    description:
      "Explore no navegador as cenas técnicas geradas no GeoOffice V2.",
    result:
      "Camadas, relevo, corte, aterro e resultados em uma leitura visual.",
    href: "#viewer-3d",
  },
] as const;
